import type {
    AccessSourceType,
    AppliedPermissions,
    PermissionDirective,
    PermissionType,
    PermissionUpdateEntry,
} from '@thinkalpha/common/contracts/permissions.js';

type PermissionSets = Pick<AppliedPermissions, 'admins' | 'owners' | 'writers' | 'readers'>;

const PERMISSION_SETS = [
    ['admin', 'admins'],
    ['owner', 'owners'],
    ['writer', 'writers'],
    ['reader', 'readers'],
] as const satisfies ReadonlyArray<readonly [PermissionType, keyof PermissionSets]>;

/** Allow-directive subject; `type` is only present on typed directives. */
export interface AllowDirectiveSubject {
    id: string;
    type?: AccessSourceType;
}

/**
 * Extract the subject of an allow directive, including the subject type when the directive
 * carries one (typed shape). Returns null for deny directives (both legacy and typed).
 */
export function getAllowDirectiveSubject(directive: PermissionDirective): AllowDirectiveSubject | null {
    if (typeof directive === 'string') return { id: directive };
    if ('mode' in directive) return directive.mode === 'allow' ? { id: directive.id, type: directive.type } : null;
    if ('deny' in directive) return null;
    return null;
}

/**
 * Extract the subject ID from any shape of permission directive.
 * Returns null for deny directives (both legacy and typed).
 */
export function getDirectiveSubjectId(directive: PermissionDirective): string | null {
    return getAllowDirectiveSubject(directive)?.id ?? null;
}

/**
 * Extract allow-only subject IDs from a permission directive array.
 */
export function getAllowSubjectIds(directives: PermissionDirective[]): string[] {
    const ids: string[] = [];
    for (const directive of directives) {
        const id = getDirectiveSubjectId(directive);
        if (id !== null) ids.push(id);
    }
    return ids;
}

/** Subject id and kind of any directive shape. */
function getDirectiveEntry(directive: PermissionDirective): { id: string; kind: 'allow' | 'deny' } {
    if (typeof directive === 'string') return { id: directive, kind: 'allow' };
    if ('mode' in directive) return { id: directive.id, kind: directive.mode };
    return { id: directive.deny, kind: 'deny' };
}

/** Every entry (allow and deny) `entityId` directly holds on `permissions`, most privileged first. */
export function getEntityEntries(permissions: PermissionSets, entityId: string): PermissionUpdateEntry[] {
    return PERMISSION_SETS.flatMap(([access, set]) =>
        permissions[set]
            .map(getDirectiveEntry)
            .filter(({ id }) => id === entityId)
            .map(({ kind }): PermissionUpdateEntry => ({ kind, access })),
    );
}

/** Access types `entityId` is directly allowed on `permissions`, most privileged first. */
export function getEntityAccessTypes(permissions: PermissionSets, entityId: string): PermissionType[] {
    return getEntityEntries(permissions, entityId)
        .filter(({ kind }) => kind === 'allow')
        .map(({ access }) => access);
}

const isAllowFor = (access: PermissionType) => (entry: PermissionUpdateEntry) =>
    entry.kind === 'allow' && entry.access === access;

/**
 * Entries `entityId` should hold after being granted `access`: its current entries plus the new
 * allow, minus the `replacing` allow when a level is being swapped. Existing deny entries are
 * carried through untouched; this never creates or removes a deny.
 */
export function entriesGranting(
    permissions: PermissionSets,
    entityId: string,
    access: PermissionType,
    replacing?: PermissionType,
): PermissionUpdateEntry[] {
    const kept = getEntityEntries(permissions, entityId).filter(
        (entry) => !(replacing !== undefined && isAllowFor(replacing)(entry)),
    );
    return kept.some(isAllowFor(access)) ? kept : [...kept, { kind: 'allow', access }];
}

/**
 * Entries `entityId` should hold after the allows for `accesses` are removed. Existing deny
 * entries are carried through untouched. Removing the last entry yields an empty list, a plain revoke.
 */
export function entriesRevoking(
    permissions: PermissionSets,
    entityId: string,
    ...accesses: PermissionType[]
): PermissionUpdateEntry[] {
    return getEntityEntries(permissions, entityId).filter(
        (entry) => !(entry.kind === 'allow' && accesses.includes(entry.access)),
    );
}
